

import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, CheckCircle2, FileText, Database, Sparkles, Plus, Pencil, Trash2, Clock, Check, X, Eye, Layers, Palette, Save, Image as ImageIcon, Layout, Loader2, Wrench, ToggleRight, ToggleLeft, AlertTriangle, History
} from 'lucide-react';
import { MigrationTool } from '@/components/MigrationTool';
import { SectorManagement } from '@/components/SectorManagement'; // Import the new component
// Corrected import path for types.ts
import { User, Article, ArticleStatus, UserRole, Sector, AppSettings, MaintenanceSettings } from '@/types'; // Corrected import path
import { DEFAULT_CONFIG } from '@/config';
import { ChatHistoryReport } from '@/components/ChatHistoryReport'; // Import the new component
import { IaUsageReport } from '@/components/IaUsageReport';

export interface AdminPanelProps {
  users: User[];
  sectors: Sector[];
  articles: Article[];
  onUpdateArticleStatus: (id: string, status: ArticleStatus) => void;
  onEditArticle: (article: Article) => void;
  onDeleteArticle: (id: string) => void;
  onViewArticle?: (article: Article) => void;
  onSaveUser: (user: User, password?: string) => Promise<any>; // Changed to Promise<any> to match new return type
  onDeleteUser: (id: string) => void;
  onSaveSector: (sector: Sector) => Promise<void>; // Ensure it returns a Promise<void>
  onDeleteSector: (id: string, newParentIdForChildren?: string | null) => Promise<void>; // Ensure it returns a Promise<void>
  onOpenCreateSectorModal: (parentId: string | null, sectorToEdit: Sector | null) => void; // Nova prop
  currentSettings?: AppSettings;
  onSaveSettings?: (settings: AppSettings) => Promise<void>;
  currentMaintenanceSettings: MaintenanceSettings; // New prop
  onSaveMaintenanceSettings: (settings: MaintenanceSettings) => Promise<void>; // New prop
  initialTab?: 'articles' | 'users' | 'sectors' | 'branding' | 'approvals' | 'migration' | 'system' | 'chat-history' | 'ia-usage'; // NEW: Add 'chat-history' & 'ia-usage'
  viewMode?: 'full' | 'approvals_only' | 'migration_only';
  currentUser: User; // NEW: Pass currentUser
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ 
  users = [], 
  sectors = [], 
  articles = [], 
  onUpdateArticleStatus, 
  onEditArticle, 
  onDeleteArticle, 
  onViewArticle,
  onSaveUser, 
  onDeleteUser, 
  onSaveSector,
  onDeleteSector,
  onOpenCreateSectorModal, // Destructure new prop
  currentSettings,
  onSaveSettings,
  currentMaintenanceSettings,
  onSaveMaintenanceSettings,
  initialTab = 'articles', 
  viewMode = 'full',
  currentUser // Destructure new prop
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [articleSearch, setArticleSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  // --- USER MODAL STATES ---
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isSubmittingUser, setIsSubmittingUser] = useState(false); // Loading state for users
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    fullName: '',
    email: '',
    sector: '',
    role: UserRole.VIEWER,
    password: '',
    mustChangePassword: true
  });
  const [userCreationSuccess, setUserCreationSuccess] = useState(false); // New state for success message
  const [error, setError] = useState<string | null>(null);
  // New state to specifically track admin session restore issues
  const [adminSessionLost, setAdminSessionLost] = useState(false);

  // --- BRANDING STATES ---
  // Inicializa o estado diretamente com as configurações do banco de dados para evitar campos em branco no mount.
  const [brandingForm, setBrandingForm] = useState<AppSettings>(() => currentSettings || {}); 
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // --- SYSTEM/MAINTENANCE STATES ---
  // Initialize maintenanceForm using a function to ensure it only runs once
  const [maintenanceForm, setMaintenanceForm] = useState<MaintenanceSettings>(() => currentMaintenanceSettings);
  const [isSavingMaintenance, setIsSavingMaintenance] = useState(false);

  // Ref to track the actual database values we synchronized last.
  // This prevents React re-render object reference changes from triggering overwrites.
  const lastSyncedBrandingProps = useRef(currentSettings);

  useEffect(() => {
    const propChanged = 
      currentSettings?.logoUrl !== lastSyncedBrandingProps.current?.logoUrl ||
      currentSettings?.loginBackgroundImageUrl !== lastSyncedBrandingProps.current?.loginBackgroundImageUrl ||
      currentSettings?.appName !== lastSyncedBrandingProps.current?.appName ||
      currentSettings?.primaryColor !== lastSyncedBrandingProps.current?.primaryColor ||
      currentSettings?.claudeInitialBalance !== lastSyncedBrandingProps.current?.claudeInitialBalance ||
      currentSettings?.claudeTotalSpent !== lastSyncedBrandingProps.current?.claudeTotalSpent ||
      currentSettings?.claudeCreditsRemaining !== lastSyncedBrandingProps.current?.claudeCreditsRemaining;

    if (propChanged && currentSettings) {
      console.log("AdminPanel: Database branding settings changed. Synchronizing local state.");
      setBrandingForm({
        ...currentSettings,
        appName: (currentSettings.appName || '').replace(/s[óo]\s+escrit[óo]rio/gi, 'SÓES') || 'SÓES Wiki'
      });
      lastSyncedBrandingProps.current = currentSettings;
    }
  }, [currentSettings]);

  // Ref to track the actual database maintenance values we synchronized last.
  const lastSyncedMaintenanceProps = useRef(currentMaintenanceSettings);

  useEffect(() => {
    const propChanged = 
      currentMaintenanceSettings.isMaintenanceMode !== lastSyncedMaintenanceProps.current.isMaintenanceMode ||
      currentMaintenanceSettings.message !== lastSyncedMaintenanceProps.current.message;

    if (propChanged) {
      console.log("AdminPanel: Database maintenance settings changed. Synchronizing local state.");
      setMaintenanceForm(currentMaintenanceSettings);
      lastSyncedMaintenanceProps.current = currentMaintenanceSettings;
    }
  }, [currentMaintenanceSettings]);

  const pendingArticles = articles.filter(a => a.status === ArticleStatus.PENDING);
  const filteredArticles = articles.filter(a => a.title.toLowerCase().includes(articleSearch.toLowerCase()));
  const filteredUsers = users.filter(u => u.fullName.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase()));

  // --- USER HANDLERS ---
  const handleOpenUserModal = (user: User | null) => {
    setIsSubmittingUser(false);
    setUserCreationSuccess(false); // Reset success message
    setError(null); // Clear error state
    setAdminSessionLost(false); // Reset admin session lost state

    if (user) {
      setEditingUser(user);
      setUserForm({
        fullName: user.fullName,
        email: user.email,
        sector: user.sector,
        role: user.role,
        password: '',
        mustChangePassword: user.mustChangePassword ?? false
      });
    } else {
      setEditingUser(null);
      setUserForm({
        fullName: '',
        email: '',
        sector: sectors.length > 0 ? sectors[0].name : '', // Default to first sector
        role: UserRole.VIEWER,
        password: '',
        mustChangePassword: true
      });
    }
    setIsUserModalOpen(true);
  };

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingUser) return;

    setIsSubmittingUser(true);
    setError(null); // Clear error state on form submission
    setAdminSessionLost(false); // Reset admin session lost state

    try {
        const userData: User = {
          id: editingUser ? editingUser.id : `new-${Date.now()}`,
          fullName: userForm.fullName,
          username: userForm.email.split('@')[0],
          email: userForm.email,
          sector: userForm.sector,
          role: userForm.role,
          mustChangePassword: userForm.mustChangePassword
        };

        const result = await onSaveUser(userData, userForm.password);
        
        if (!editingUser) { // Only for new user creation
            setUserCreationSuccess(true);
            if (!result.adminSessionRestored) {
                setAdminSessionLost(true);
                setError('⚠️ Sua sessão de administrador foi perdida. Você precisará fazer login novamente.');
                // Do NOT close modal if admin session is lost, let user see the error
            } else {
                // If both success and admin session restored, auto-close after delay
                setTimeout(() => {
                    setIsUserModalOpen(false);
                    setUserCreationSuccess(false);
                    setAdminSessionLost(false); // Ensure this is reset
                }, 2500); 
            }
        } else { // For existing user edits, close immediately
            alert("Usuário atualizado com sucesso!");
            setIsUserModalOpen(false);
        }

    } catch (error: any) {
        console.error("Erro ao salvar usuário:", error);
        setError(error.message || `Erro ao salvar usuário: ${error.message}`);
        setAdminSessionLost(false); // Ensure this is reset if it's a different error
    } finally {
        setIsSubmittingUser(false);
    }
  };

  const handleDeleteUserClick = (id: string) => {
      if (window.confirm("❗ ATENÇÃO: Tem certeza que deseja remover este usuário permanentemente?\n\nEsta ação não pode ser desfeita.")) {
          onDeleteUser(id);
      }
  };

  // --- BRANDING HANDLERS ---
  const handleSaveBranding = async () => {
    if (onSaveSettings) {
        setIsSavingSettings(true);
        try {
            // Ensure to pass an object that matches the AppSettings type
            const cleanAppName = (brandingForm.appName || 'SÓES Wiki').replace(/s[óo]\s+escrit[óo]rio/gi, 'SÓES');
            await onSaveSettings({ ...brandingForm, appName: cleanAppName, lastUpdatedAt: Date.now() }); 
            alert('Configurações visuais salvas com sucesso!');
        } catch (err: any) {
            console.error("Erro ao salvar configurações:", err);
            alert("Erro ao salvar configurações: " + err.message);
        } finally {
            setIsSavingSettings(false);
        }
    }
  };

  // --- MAINTENANCE HANDLERS ---
  const handleSaveMaintenance = async () => {
    setIsSavingMaintenance(true);
    console.log("AdminPanel: Clicou em Salvar Manutenção. Enviando para o DB:", maintenanceForm);
    try {
        await onSaveMaintenanceSettings(maintenanceForm);
        alert('Configurações de manutenção salvas com sucesso!');
        console.log("AdminPanel: Configurações de manutenção salvas com sucesso no DB.");
    } catch (error: any) {
        console.error("AdminPanel: ERRO ao salvar configurações de manutenção:", error);
        alert("Erro ao salvar configurações de manutenção: " + error.message);
    } finally {
        setIsSavingMaintenance(false);
    }
  };

  const getSafeUrl = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    const safeUrl = url.trim().toLowerCase();
    if (safeUrl.startsWith('http://') || safeUrl.startsWith('https://') || safeUrl.startsWith('data:image/')) {
        return url;
    }
    return undefined;
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-20 animate-fade-in">
      {/* Header com Status da IA Simplificado */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
        <div>
          <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">
            {viewMode === 'approvals_only' ? 'Central de Aprovações' : viewMode === 'migration_only' ? 'Migração de Conteúdo' : 'Painel Administrativo'}
          </h2>
          <p className="text-slate-500 text-sm">
             {viewMode === 'approvals_only' ? 'Gerencie as publicações pendentes.' : viewMode === 'migration_only' ? 'Importe dados do WordPress.' : 'Gestão completa da plataforma.'}
          </p>
        </div>
        {viewMode === 'full' && (
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-2xl border text-[10px] font-black uppercase tracking-widest ${((currentSettings?.claudeCreditsRemaining ?? 0) <= 2) ? 'bg-red-50 border-red-100 text-red-700' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
                  <Sparkles size={14} />
                  Saldo Claude: $ {(currentSettings?.claudeCreditsRemaining ?? 0).toFixed(2)}
              </div>
            </div>
        )}
      </div>

      {/* Navegação de Abas (Apenas no Modo Full) */}
      {viewMode === 'full' && (
        <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm inline-flex flex-wrap gap-1">
          <button onClick={() => setActiveTab('articles')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'articles' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><FileText size={16} /> Acervo</button>
          <button onClick={() => setActiveTab('users')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><Users size={16} /> Usuários</button>
          <button onClick={() => setActiveTab('sectors')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'sectors' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><Layers size={16} /> Setores</button>
          <button onClick={() => setActiveTab('branding')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'branding' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><Palette size={16} /> Personalização</button>
          <button onClick={() => setActiveTab('system')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'system' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><Wrench size={16} /> Sistema</button>
          <button onClick={() => setActiveTab('chat-history')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'chat-history' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><History size={16} /> Histórico de IA</button>
          <button onClick={() => setActiveTab('ia-usage')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'ia-usage' ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-500 hover:bg-slate-50'}`}><Sparkles size={16} /> Consumo de IA</button>
        </div>
      )}

      {/* ABA 1: ACERVO GERAL */}
      {activeTab === 'articles' && viewMode === 'full' && (
        <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
           <div className="p-8 border-b flex justify-between items-center bg-slate-50/30">
             <h3 className="font-black text-slate-800 uppercase">Biblioteca de Conhecimento</h3>
             <input value={articleSearch} onChange={(e) => setArticleSearch(e.target.value)} placeholder="Pesquisar artigos..." className="px-5 py-2.5 bg-white border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 w-64 shadow-sm" />
           </div>
           <div className="overflow-x-auto">
             <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-400 font-black uppercase text-[10px] tracking-widest border-b">
                  <tr><th className="px-8 py-4 text-left">Título</th><th className="px-8 py-4 text-left">Dono/Autor</th><th className="px-8 py-4 text-center">Status</th><th className="px-8 py-4 text-right">Ações</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredArticles.length === 0 ? (
                    <tr><td colSpan={4} className="px-8 py-10 text-center text-slate-400 font-medium">Nenhum artigo encontrado.</td></tr>
                  ) : filteredArticles.map((a: Article) => (
                    <tr key={a.id} className="hover:bg-slate-50 group">
                      <td className="px-8 py-5 font-bold text-slate-700">{a.title}</td>
                      <td className="px-8 py-5 text-slate-500 font-medium">{a.authorName}</td>
                      <td className="px-8 py-5 text-center">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${a.status === ArticleStatus.APPROVED ? 'bg-green-50 text-green-600 border-green-100' : 'bg-orange-50 text-orange-600 border-orange-100'}`}>{a.status}</span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => onViewArticle && onViewArticle(a)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors" title="Visualizar"><Eye size={18}/></button>
                          <button onClick={() => onEditArticle(a)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar"><Pencil size={18}/></button>
                          <button onClick={() => onDeleteArticle(a.id)} className="p-2 text-red-200 hover:text-red-500 transition-colors" title="Excluir"><Trash2 size={18}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
             </table>
           </div>
        </div>
      )}

      {/* VIEWMODE: APROVAÇÕES (Ou quando chamado via Sidebar) */}
      {(activeTab === 'approvals' || viewMode === 'approvals_only') && (
        <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
           <div className="p-8 border-b bg-orange-50/30 flex items-center gap-4">
             <div className="p-3 bg-white rounded-2xl shadow-sm text-orange-600"><Clock size={24} /></div>
             <div>
               <h3 className="font-black text-slate-800 uppercase tracking-tighter">Aprovações Pendentes</h3>
               <p className="text-slate-500 text-sm">Artigos que aguardam revisão para serem publicados.</p>
             </div>
           </div>
           <div className="overflow-x-auto">
             <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-400 font-black uppercase text-[10px] tracking-widest border-b">
                  <tr><th className="px-8 py-4 text-left">Título</th><th className="px-8 py-4 text-left">Autor</th><th className="px-8 py-4 text-left">Data</th><th className="px-8 py-4 text-right">Decisão</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendingArticles.length === 0 ? (
                    <tr><td colSpan={4} className="px-8 py-20 text-center">
                      <CheckCircle2 className="mx-auto text-green-200 mb-4" size={48} />
                      <p className="font-bold text-slate-400">Tudo em dia! Nenhuma aprovação pendente.</p>
                    </td></tr>
                  ) : pendingArticles.map((a: Article) => (
                    <tr key={a.id} className="hover:bg-slate-50 group">
                      <td className="px-8 py-5">
                         <div className="font-bold text-slate-800">{a.title}</div>
                         <div className="text-[10px] text-slate-400 font-black uppercase">{a.sectorName}</div>
                      </td>
                      <td className="px-8 py-5 text-slate-500 font-medium">{a.authorName}</td>
                      <td className="px-8 py-5 text-slate-400 text-xs">{new Date(a.createdAt).toLocaleDateString()}</td>
                      <td className="px-8 py-5 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                             onClick={() => onViewArticle && onViewArticle(a)} 
                             className="bg-blue-50 text-blue-600 p-2 rounded-xl hover:bg-blue-100 shadow-sm transition-all mr-2" 
                             title="Ler Artigo"
                          >
                            <Eye size={18} />
                          </button>
                          <button onClick={() => onUpdateArticleStatus(a.id, ArticleStatus.APPROVED)} className="bg-green-600 text-white p-2 rounded-xl hover:bg-green-700 shadow-md transition-all" title="Aprovar"><Check size={18}/></button>
                          <button onClick={() => onUpdateArticleStatus(a.id, ArticleStatus.REJECTED)} className="bg-red-500 text-white p-2 rounded-xl hover:bg-red-600 shadow-md transition-all" title="Rejeitar"><X size={18}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
             </table>
           </div>
        </div>
      )}

      {/* ABA 2: USUÁRIOS */}
      {activeTab === 'users' && viewMode === 'full' && (
        <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
           <div className="p-8 border-b flex justify-between items-center bg-slate-50/30">
             <h3 className="font-black text-slate-800 uppercase">Gestão de Usuários</h3>
             <button onClick={() => handleOpenUserModal(null)} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-lg hover:bg-blue-700 transition-all"><Plus size={16} /> Novo Usuário</button>
           </div>
           <div className="overflow-x-auto">
             <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-400 font-black uppercase text-[10px] tracking-widest border-b">
                  <tr><th className="px-8 py-4 text-left">Membro</th><th className="px-8 py-4 text-left">Setor</th><th className="px-8 py-4 text-left">Permissão</th><th className="px-8 py-4 text-right">Ações</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((u: User) => (
                    <tr key={u.id} className="hover:bg-slate-50 group">
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(u.fullName)}&background=random`} className="w-10 h-10 rounded-xl" alt="" />
                          <div><p className="font-bold text-slate-800">{u.fullName}</p><p className="text-[10px] text-slate-400 font-bold uppercase">{u.email}</p></div>
                        </div>
                      </td>
                      <td className="px-8 py-5"><span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">{u.sector}</span></td>
                      <td className="px-8 py-5 text-slate-600 font-bold text-xs">{u.role}</td>
                      <td className="px-8 py-5 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => handleOpenUserModal(u)} className="p-2 text-slate-400 hover:text-blue-600"><Pencil size={18}/></button>
                          <button onClick={() => handleDeleteUserClick(u.id)} className="p-2 text-red-200 hover:text-red-500 transition-colors" title="Excluir"><Trash2 size={18}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
             </table>
           </div>
        </div>
      )}

      {/* ABA 3: SETORES (AGORA USA O NOVO COMPONENTE) */}
      {activeTab === 'sectors' && viewMode === 'full' && (
        <SectorManagement 
          sectors={sectors} 
          onSaveSector={onSaveSector} 
          onDeleteSector={onDeleteSector} 
          onOpenCreateSectorModal={onOpenCreateSectorModal} // Pass to allow editing via modal
          currentUser={currentUser}
        />
      )}

      {/* ABA 4: PERSONALIZAÇÃO */}
      {activeTab === 'branding' && viewMode === 'full' && (
        <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
           <div className="p-8 border-b flex justify-between items-center bg-slate-50/30">
             <div>
               <h3 className="font-black text-slate-800 uppercase">Personalização Visual</h3>
               <p className="text-slate-500 text-sm">Altere a aparência da plataforma.</p>
             </div>
             <button 
                onClick={handleSaveBranding} 
                disabled={isSavingSettings}
                className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50"
             >
                <Save size={16} /> {isSavingSettings ? 'Salvando...' : 'Salvar Alterações'}
             </button>
           </div>
           
           <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-12">
               <div className="space-y-6">
                   <div>
                       <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Logo da Empresa (URL)</label>
                       <div className="flex items-center gap-2">
                           <ImageIcon className="text-slate-400 w-5 h-5" />
                           <input 
                             value={brandingForm.logoUrl || ''} // Ensure controlled component does not receive undefined
                             onChange={(e) => setBrandingForm({...brandingForm, logoUrl: e.target.value})}
                             className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" 
                             placeholder="https://..."
                           />
                       </div>
                       <p className="text-xs text-slate-400 mt-2">Recomendado: PNG Transparente.</p>
                   </div>

                   <div>
                       <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Fundo da Tela de Login (URL)</label>
                       <div className="flex items-center gap-2">
                           <Layout className="text-slate-400 w-5 h-5" />
                           <input 
                             value={brandingForm.loginBackgroundImageUrl || ''} // Ensure controlled component does not receive undefined
                             onChange={(e) => setBrandingForm({...brandingForm, loginBackgroundImageUrl: e.target.value})}
                             className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" 
                             placeholder="https://..."
                           />
                       </div>
                       <p className="text-xs text-slate-400 mt-2">Recomendado: Imagem de alta resolução (1920x1080).</p>
                   </div>
               </div>

               <div className="bg-slate-100 rounded-3xl p-6 border border-slate-200 flex flex-col items-center justify-center text-center">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Pré-visualização do Logo</h4>
                    {getSafeUrl(brandingForm.logoUrl) ? (
                        <img 
                            src={getSafeUrl(brandingForm.logoUrl)} 
                            alt="Preview" 
                            className="max-h-20 object-contain mb-4" 
                            onError={(e) => {
                                console.error('AdminPanel: Logo preview failed to load:', brandingForm.logoUrl, e);
                                // REMOVIDO: e.currentTarget.style.display = 'none'; // Esconde a imagem se falhar no preview
                            }} 
                        />
                    ) : (
                        <div className="w-20 h-20 bg-slate-200 rounded-xl flex items-center justify-center mb-4 text-slate-400 font-bold">
                            {brandingForm.appName || 'No App Name'} {/* Usa appName como fallback */}
                        </div>
                    )}
                    <p className="text-slate-500 text-sm">Assim que aparecerá no topo do menu.</p>
               </div>
            </div>

            {/* CONFIGURAÇÃO CLAUDE API */}
            <div className="p-8 border-t border-slate-100 bg-slate-50/20 space-y-6">
                <div className="flex items-center gap-2 text-slate-800">
                    <Sparkles className="text-blue-600" size={20} />
                    <h4 className="text-sm font-black uppercase tracking-widest">Controle de Saldo - API Claude</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Saldo Inicial Recarregado (USD)</label>
                        <input 
                          type="number"
                          step="0.01"
                          min="0"
                          value={brandingForm.claudeInitialBalance ?? 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setBrandingForm({
                              ...brandingForm, 
                              claudeInitialBalance: val,
                              claudeCreditsRemaining: Math.max(0, val - (brandingForm.claudeTotalSpent || 0))
                            });
                          }}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold text-slate-700" 
                          placeholder="0.00"
                        />
                        <p className="text-xs text-slate-400 mt-2">Valor em dólares inserido como crédito pré-pago.</p>
                    </div>
                    <div>
                        <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Total Gasto Estimado (USD)</label>
                        <div className="flex gap-2">
                            <input 
                              type="number"
                              step="0.000001"
                              disabled
                              value={brandingForm.claudeTotalSpent ?? 0}
                              className="flex-1 px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl outline-none text-sm font-bold text-slate-500" 
                              placeholder="0.00"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm("Deseja zerar o contador de total gasto com a IA Claude?")) {
                                  setBrandingForm({
                                    ...brandingForm,
                                    claudeTotalSpent: 0,
                                    claudeCreditsRemaining: brandingForm.claudeInitialBalance || 0
                                  });
                                }
                              }}
                              className="px-3 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-black uppercase border border-red-100 hover:bg-red-100 transition-all shadow-sm"
                            >
                              Zerar
                            </button>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Calculado com base no consumo real de tokens.</p>
                    </div>
                    <div>
                        <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Saldo Restante Estimado (USD)</label>
                        <div className={`px-4 py-3 border rounded-xl text-sm font-bold flex items-center justify-between h-[46px] ${((brandingForm.claudeCreditsRemaining ?? 0) <= 2) ? 'bg-red-50 border-red-100 text-red-700' : 'bg-green-50 border-green-100 text-green-700'}`}>
                            <span>$ {(brandingForm.claudeCreditsRemaining ?? 0).toFixed(2)}</span>
                            <span className="text-[10px] uppercase font-black tracking-wider">
                              {((brandingForm.claudeCreditsRemaining ?? 0) <= 2) ? 'Abaixo do Recomendado' : 'Saudável'}
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Saldo projetado após o consumo acumulado.</p>
                    </div>
                </div>
            </div>

            <div className="p-8 border-t border-slate-100 flex justify-end bg-slate-50/20">
              <button 
                 onClick={handleSaveBranding} 
                 disabled={isSavingSettings}
                 className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50"
              >
                 <Save size={16} /> {isSavingSettings ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
        </div>
      )}

      {/* ABA 5: SISTEMA (Manutenção) */}
      {activeTab === 'system' && viewMode === 'full' && (
        <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
           <div className="p-8 border-b flex justify-between items-center bg-slate-50/30">
             <div>
               <h3 className="font-black text-slate-800 uppercase">Configurações do Sistema</h3>
               <p className="text-slate-500 text-sm">Controle o acesso e a disponibilidade da plataforma.</p>
             </div>
             <button 
                onClick={handleSaveMaintenance} 
                disabled={isSavingMaintenance}
                className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50"
             >
                <Save size={16} /> {isSavingMaintenance ? 'Salvando...' : 'Salvar Alterações'}
             </button>
           </div>
           
           <div className="p-8 space-y-8">
               <div>
                   <label htmlFor="maintenance-mode" className="flex items-center justify-between text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">
                     <span>Modo de Manutenção</span>
                     <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-black ${maintenanceForm.isMaintenanceMode ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                         {maintenanceForm.isMaintenanceMode ? 'Ativo' : 'Inativo'}
                     </span>
                   </label>
                   <div className="relative flex items-center">
                       <input 
                         type="checkbox" 
                         id="maintenance-mode"
                         checked={maintenanceForm.isMaintenanceMode} // Bound to local state
                         onChange={(e) => {
                           console.log("AdminPanel: Toggle maintenance mode clicked. New state in local form:", e.target.checked);
                           setMaintenanceForm(prev => ({...prev, isMaintenanceMode: e.target.checked}));
                         }}
                         className="hidden"
                       />
                       <label htmlFor="maintenance-mode" className="relative block w-14 h-8 rounded-full cursor-pointer transition-colors duration-200 ease-in-out"
                              style={{ backgroundColor: maintenanceForm.isMaintenanceMode ? '#ef4444' : '#22c55e' }}
                       >
                           <span className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-200 ease-in-out"
                                 style={{ left: maintenanceForm.isMaintenanceMode ? '28px' : '4px' }}></span>
                       </label>
                       <span className="ml-4 text-sm text-slate-700">Ative para bloquear o acesso ao sistema.</span>
                   </div>
               </div>

               <div>
                   <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Mensagem de Manutenção</label>
                   <textarea 
                     value={maintenanceForm.message} 
                     onChange={(e) => setMaintenanceForm({...maintenanceForm, message: e.target.value})}
                     className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 min-h-[120px]" 
                     placeholder="A Wiki está passando por atualizações..."
                   />
                   <p className="text-xs text-slate-400 mt-2">Esta mensagem será exibida na tela de login quando o modo de manutenção estiver ativo.</p>
               </div>
           </div>
        </div>
      )}

      {/* FERRAMENTA DE MIGRAÇÃO */}
      {(activeTab === 'migration' || viewMode === 'migration_only') && (
        <MigrationTool sectors={sectors} users={users} />
      )}

      {/* NOVA ABA: HISTÓRICO DE IA */}
      {activeTab === 'chat-history' && viewMode === 'full' && (
        <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in h-[70vh]"> {/* Ajuste a altura conforme necessário */}
          <div className="p-8 border-b bg-blue-50/30 flex items-center gap-4">
            <div className="p-3 bg-white rounded-2xl shadow-sm text-blue-600"><History size={24} /></div>
            <div>
              <h3 className="font-black text-slate-800 uppercase tracking-tighter">Histórico de Conversas da IA</h3>
              <p className="text-slate-500 text-sm">Visualize as interações dos usuários com a Inteligência Artificial.</p>
            </div>
          </div>
          <ChatHistoryReport users={users} currentUser={currentUser} />
        </div>
      )}

      {activeTab === 'ia-usage' && viewMode === 'full' && (
        <IaUsageReport users={users} />
      )}

      {/* --- MODAL DE USUÁRIOS --- */}
      {isUserModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsUserModalOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-black text-slate-800 uppercase">{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <button onClick={() => setIsUserModalOpen(false)}><X className="text-slate-400 hover:text-slate-600" /></button>
            </div>
            {userCreationSuccess && !adminSessionLost ? ( // Show success ONLY if session was restored
                <div className="text-center py-10 px-6 bg-green-50 border border-green-100 rounded-b-3xl animate-fade-in">
                    <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                    <h4 className="text-2xl font-bold text-green-800 mb-2">Usuário Criado!</h4>
                    <p className="text-green-700 text-lg">"{userForm.fullName}" foi cadastrado com sucesso.</p>
                    <p className="text-green-700 text-sm mt-2">Ele(a) precisará fazer login com a senha inicial e alterá-la.</p>
                </div>
            ) : (
                <form onSubmit={handleSubmitUser} className="p-8 space-y-6">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nome Completo</label>
                    <input required value={userForm.fullName} onChange={e => setUserForm({...userForm, fullName: e.target.value})} className="w-full p-3 bg-slate-50 border rounded-xl" />
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">E-mail Corporativo</label>
                    <input type="email" required value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} className="w-full p-3 bg-slate-50 border rounded-xl" disabled={!!editingUser} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Setor</label>
                        <select value={userForm.sector} onChange={e => setUserForm({...userForm, sector: e.target.value})} className="w-full p-3 bg-slate-50 border rounded-xl">
                            {sectors.map((s: Sector) => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Permissão</label>
                        <select value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value as UserRole})} className="w-full p-3 bg-slate-50 border rounded-xl">
                            {Object.values(UserRole).map((r: UserRole) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                </div>
                
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{editingUser ? 'Redefinir Senha (Opcional)' : 'Senha Inicial'}</label>
                    <input 
                        type="password" 
                        value={userForm.password} 
                        onChange={e => setUserForm({...userForm, password: e.target.value})} 
                        className="w-full p-3 bg-slate-50 border rounded-xl" 
                        placeholder={editingUser ? "Deixe em branco para manter" : "Mínimo 6 caracteres"}
                        required={!editingUser && !userCreationSuccess} // Senha é obrigatória apenas para novos usuários
                        minLength={6}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <input 
                    type="checkbox" 
                    id="mustChange"
                    checked={userForm.mustChangePassword}
                    onChange={e => setUserForm({...userForm, mustChangePassword: e.target.checked})}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="mustChange" className="text-sm text-slate-600">O usuário deve alterar a senha no próximo login?</label>
                </div>

                {/* Always display error if present */}
                {error && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2 border border-red-100">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                    </div>
                )}

                <button 
                    type="submit" 
                    disabled={isSubmittingUser}
                    className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold uppercase tracking-widest hover:bg-blue-700 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
                >
                    {isSubmittingUser ? <Loader2 className="animate-spin w-5 h-5" /> : null}
                    {isSubmittingUser ? 'Salvando...' : 'Salvar Usuário'}
                </button>
                </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};